/**
 * OrbitDBSyncProvider — durable, replicated envelope log over OrbitDB v3+.
 *
 * Each `streamId` maps to an OrbitDB events database. The events DB provides:
 *
 *   - Append-only ordered log of arbitrary values (we store SignedEnvelopes)
 *   - CRDT (Conflict-free Replicated Data Type) replication via libp2p pubsub
 *   - Persistent storage via Helia (IPFS) blockstore
 *
 * Two layers of signing per envelope:
 *
 *   1. Application layer: `SignedEnvelope` carries an Ed25519 signature from
 *      our `SignerIdentity` for the message's context. This is the
 *      cryptographic source of truth for application authenticity.
 *
 *   2. OrbitDB layer: OrbitDB signs each log entry with its own per-device
 *      identity. This serves ACL and replication-time integrity, not
 *      application authenticity. See `MeshNode.ts` header for the rationale.
 *
 * The provider delegates log opening to `MeshNode.openLog(streamId)`, which
 * doesn't take a context — the OrbitDB instance has a single identity.
 * Per-context unlinkability is preserved at the envelope layer.
 *
 * Context still flows through `SyncProvider.open(streamId, context)` so the
 * abstraction can be implemented by other backends (e.g., a future Waku
 * provider) that DO support per-context identity. We just don't use it
 * inside the OrbitDB implementation.
 */

import type { SignedEnvelope } from '../envelope/Envelope';
import type { Context } from '../identity';
import type { EventLog, EventLogEntry, MeshNode } from './MeshNode';
import type { SyncProvider, SyncStreamHandle } from './SyncProvider';

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

interface OpenStream {
  log: EventLog;
  context: Context;
  subscribers: Set<(e: SignedEnvelope) => void>;
  headHash: string;
  /**
   * Local sequence counter for ordering. OrbitDB's events DB doesn't expose
   * a clock per entry directly (clocks are inside the underlying oplog and
   * not on the events DB surface), so we track our own append count for
   * the head-tracking semantics expected by SyncStreamHandle.
   */
  headSeq: number;
  updateHandler: (entry: EventLogEntry) => void;
}

export class OrbitDBSyncProvider implements SyncProvider {
  #node: MeshNode;
  #streams = new Map<string, OpenStream>();
  #started = false;

  constructor(node: MeshNode) {
    this.#node = node;
  }

  async start(): Promise<void> {
    if (this.#started) return;
    if (!this.#node.started) await this.#node.start();
    this.#started = true;
  }

  async stop(): Promise<void> {
    // Close streams first so OrbitDB's internal cleanup runs in the right order.
    const ids = [...this.#streams.keys()];
    for (const id of ids) {
      await this.close(id);
    }
    this.#started = false;
  }

  async open(streamId: string, context: Context): Promise<SyncStreamHandle> {
    if (!this.#started) throw new Error('OrbitDBSyncProvider not started');

    const existing = this.#streams.get(streamId);
    if (existing) {
      return {
        streamId,
        context: existing.context,
        head: existing.headHash,
        lamport: existing.headSeq,
      };
    }

    const log = await this.#node.openLog(streamId);

    const open: OpenStream = {
      log,
      context,
      subscribers: new Set(),
      headHash: '',
      headSeq: 0,
      updateHandler: (entry: EventLogEntry) => {
        const env = entry.value;
        open.headSeq += 1;
        open.headHash = env.hash;
        open.subscribers.forEach((cb) => cb(env));
      },
    };

    log.events.on('update', open.updateHandler);
    this.#streams.set(streamId, open);

    // Backfill head from existing entries. `all()` returns the full set;
    // for performance we'd rather use `iterator({ amount: 1 })` but iterating
    // backwards isn't supported directly, so we walk forward and keep the
    // last one. The events DB stores entries newest-first in iteration order
    // by default per OrbitDB docs, so the first entry IS the latest.
    let count = 0;
    let last: { hash: string; value: SignedEnvelope } | null = null;
    for await (const [hash, value] of log.iterator()) {
      count += 1;
      // First yield from the iterator is the latest entry.
      if (count === 1) {
        last = { hash, value };
      }
    }
    if (last) {
      open.headHash = last.value.hash;
      open.headSeq = count;
    }

    return {
      streamId,
      context,
      head: open.headHash,
      lamport: open.headSeq,
    };
  }

  async append(streamId: string, envelope: SignedEnvelope): Promise<void> {
    const stream = this.#streams.get(streamId);
    if (!stream) throw new Error(`Stream ${streamId} not open`);
    await stream.log.add(envelope);
    // OrbitDB v3 emits 'update' for local writes too, which triggers
    // updateHandler and fans out to subscribers. We don't double-emit here.
  }

  subscribe(streamId: string, onEnvelope: (e: SignedEnvelope) => void): () => void {
    const stream = this.#streams.get(streamId);
    if (!stream) throw new Error(`Stream ${streamId} not open`);
    // Future-only delivery. Past history is read via log.iterator() by
    // callers that explicitly want it (matches OrbitDB's update-event model).
    stream.subscribers.add(onEnvelope);
    return () => stream.subscribers.delete(onEnvelope);
  }

  async pull(streamId: string): Promise<void> {
    // OrbitDB v3 replication is automatic via gossipsub. The 'join' event
    // on the log fires when a new peer connects with their heads — that's
    // when sync happens. There's no explicit "pull now" API needed for
    // alpha+; the gossipsub mesh handles it.
    void streamId;
  }

  async close(streamId: string): Promise<void> {
    const stream = this.#streams.get(streamId);
    if (!stream) return;
    // OrbitDB's events.off signature is loose; the cast is correct at runtime.
    stream.log.events.off('update', stream.updateHandler as never);
    await stream.log.close();
    stream.subscribers.clear();
    this.#streams.delete(streamId);
  }

  async peerCount(streamId: string): Promise<number> {
    const stream = this.#streams.get(streamId);
    if (!stream) return 0;
    // OrbitDB v3 exposes `db.peers` as a Set of connected peers for this
    // database specifically — exactly what we want.
    return stream.log.peers.size;
  }
}
